import { useCallback, useEffect, useState } from 'react'
import { Mail, Phone, RefreshCw } from 'lucide-react'
import { closeEnquiry, replyToEnquiry } from '../../lib/admin-command'
import { enquiryWorkspaceStatus, type EnquiryWorkspaceStatus } from '../../lib/admin-workspace'
import { formatEnquirySource, formatServiceInterestLabels } from '../../lib/detailing-quote'
import { supabase } from '../../lib/supabase'
import { AdminSidebar } from '../admin/admin-sidebar'
import { PageTitle } from '../seo/page-title'

interface QuoteRow {
  id: string
  public_reference: string
  created_at: string
  updated_at: string
  status: string
  customer_name: string
  customer_email: string
  customer_phone: string
  preferred_contact_method: string | null
  vehicle_type: string
  vehicle_make: string
  vehicle_model: string
  vehicle_year: string
  vehicle_colour: string
  registration: string | null
  service_interests_json: string[] | null
  desired_outcome: string | null
  current_condition: string | null
  customer_notes: string | null
  internal_notes: string | null
  source: string | null
}

interface EnquiryMessage {
  id: string
  channel: string | null
  subject: string | null
  body: string | null
  status: string
  recipient: string
  created_at: string
  sent_at: string | null
  error_message: string | null
}

function serviceSummary(keys: string[] | null) {
  return formatServiceInterestLabels(keys)
}

function vehicleSummary(row: QuoteRow) {
  return [row.vehicle_year, row.vehicle_colour, row.vehicle_make, row.vehicle_model, `(${row.vehicle_type})`].filter(Boolean).join(' ')
}

export function AdminQuotes({
  enquiryReference,
}: Readonly<{
  enquiryReference?: string
}>) {
  const [quotes, setQuotes] = useState<QuoteRow[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [messages, setMessages] = useState<EnquiryMessage[]>([])
  const [reply, setReply] = useState('')
  const [filter, setFilter] = useState<EnquiryWorkspaceStatus | 'all'>('all')
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const selectedQuote = quotes.find((quote) => quote.id === selectedId) ?? null

  const loadQuotes = useCallback(async () => {
    setStatus('loading')
    setMessage(null)
    const { data, error } = await supabase
      .from('quote_requests')
      .select('id, public_reference, created_at, updated_at, status, customer_name, customer_email, customer_phone, preferred_contact_method, vehicle_type, vehicle_make, vehicle_model, vehicle_year, vehicle_colour, registration, service_interests_json, desired_outcome, current_condition, customer_notes, internal_notes, source')
      .order('created_at', { ascending: false })
      .limit(100)

    if (error) {
      setMessage(error.message)
      setStatus('error')
      return
    }

    setQuotes((data ?? []) as QuoteRow[])
    setStatus('ready')
  }, [])

  const loadMessages = useCallback(async (enquiryId: string) => {
    const { data, error } = await supabase
      .from('customer_communications')
      .select('id, channel, subject, body, status, recipient, created_at, sent_at, error_message')
      .eq('quote_request_id', enquiryId)
      .order('created_at', { ascending: false })
    if (error) {
      setMessage(error.message)
      return
    }
    setMessages((data ?? []) as EnquiryMessage[])
  }, [])

  useEffect(() => {
    void loadQuotes()
  }, [loadQuotes])

  const filteredQuotes = quotes.filter((quote) => (
    filter === 'all' || enquiryWorkspaceStatus(quote.status) === filter
  ))

  useEffect(() => {
    if (enquiryReference) {
      const match = quotes.find((quote) => quote.public_reference === enquiryReference)
      if (match) {
        setSelectedId(match.id)
        return
      }
    }
    setSelectedId((current) => {
      const visible = quotes.filter((quote) => (
        filter === 'all' || enquiryWorkspaceStatus(quote.status) === filter
      ))
      if (current && visible.some((quote) => quote.id === current)) return current
      return visible[0]?.id ?? null
    })
  }, [enquiryReference, filter, quotes])

  useEffect(() => {
    if (!selectedQuote) {
      setMessages([])
      return
    }
    void loadMessages(selectedQuote.id)
  }, [loadMessages, selectedQuote])

  async function sendReply() {
    if (!selectedQuote) return
    setBusy(true)
    setMessage(null)
    try {
      const result = await replyToEnquiry(selectedQuote.id, reply)
      setReply('')
      setMessage(result.sendStatus === 'skipped'
        ? 'Reply was recorded but email sending is not configured.'
        : `Email sent to the stored enquiry address.`)
      await loadQuotes()
      await loadMessages(selectedQuote.id)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The enquiry email was not sent.')
    } finally {
      setBusy(false)
    }
  }

  async function markClosed() {
    if (!selectedQuote) return
    setBusy(true)
    setMessage(null)
    try {
      await closeEnquiry(selectedQuote.id)
      setMessage('Enquiry closed.')
      await loadQuotes()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to close the enquiry.')
    } finally {
      setBusy(false)
    }
  }

  const workspaceStatus = selectedQuote ? enquiryWorkspaceStatus(selectedQuote.status) : 'new'

  return (
    <div className="admin-dashboard">
      <PageTitle title="Enquiries | AUTOGLOW Detailing Admin" />
      <AdminSidebar active="quotes" />
      <main>
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Enquiries</p>
            <h1>Quote Requests</h1>
          </div>
          <button type="button" onClick={() => void loadQuotes()} disabled={status === 'loading'}>
            <RefreshCw size={17} /> Refresh
          </button>
        </header>

        {message ? <p className="admin-alert">{message}</p> : null}

        <section className="admin-record-grid">
          <article className="admin-card admin-record-list">
            <h2>Requests</h2>
            {quotes.length === 0 ? <p className="admin-empty-state">No structured quote requests found.</p> : null}
            <div className="admin-filter-bar">
              {(['all', 'new', 'replied', 'closed'] as const).map((item) => (
                <button
                  className={filter === item ? 'active' : undefined}
                  key={item}
                  onClick={() => setFilter(item)}
                  type="button"
                >
                  {item === 'all' ? 'All' : item[0].toUpperCase() + item.slice(1)}
                </button>
              ))}
            </div>
            {filteredQuotes.map((quote) => (
              <button className={selectedQuote?.id === quote.id ? 'selected' : ''} type="button" key={quote.id} onClick={() => setSelectedId(quote.id)}>
                <strong>{quote.public_reference}</strong>
                <span>{quote.customer_name} - {vehicleSummary(quote)}</span>
                <small>{formatEnquirySource(quote.source)} / {serviceSummary(quote.service_interests_json)} / {enquiryWorkspaceStatus(quote.status) === 'new' ? 'New' : enquiryWorkspaceStatus(quote.status) === 'replied' ? 'Replied' : 'Closed'}</small>
              </button>
            ))}
          </article>

          <article className="admin-card admin-record-detail">
            {selectedQuote ? (
              <>
                <div className="admin-card-heading">
                  <div>
                    <h2>{selectedQuote.public_reference}</h2>
                    <p>{selectedQuote.customer_name} - {vehicleSummary(selectedQuote)}</p>
                  </div>
                <span className="status-pill">{workspaceStatus === 'new' ? 'New' : workspaceStatus === 'replied' ? 'Replied' : 'Closed'}</span>
                </div>
                <div className="booking-contact-actions">
                  <a href={`tel:${selectedQuote.customer_phone}`}><Phone size={15} /> Call</a>
                  <a href={`mailto:${selectedQuote.customer_email}?subject=Autoglow quote ${selectedQuote.public_reference}`}><Mail size={15} /> Email</a>
                  {workspaceStatus !== 'closed' ? (
                    <button disabled={busy} type="button" onClick={() => void markClosed()}>Close enquiry</button>
                  ) : null}
                </div>
                <dl className="review-list enquiry-detail">
                  <div><dt>Customer</dt><dd>{selectedQuote.customer_name}</dd></div>
                  <div><dt>Email</dt><dd>{selectedQuote.customer_email}</dd></div>
                  <div><dt>Phone</dt><dd>{selectedQuote.customer_phone}</dd></div>
                  <div><dt>Preferred contact</dt><dd>{selectedQuote.preferred_contact_method || 'Phone'}</dd></div>
                  <div><dt>Source</dt><dd>{formatEnquirySource(selectedQuote.source)}</dd></div>
                  <div><dt>Vehicle</dt><dd>{vehicleSummary(selectedQuote)}</dd></div>
                  <div><dt>Registration</dt><dd>{selectedQuote.registration || 'Not provided'}</dd></div>
                  <div><dt>Selected services</dt><dd>{serviceSummary(selectedQuote.service_interests_json)}</dd></div>
                  <div><dt>Desired outcome</dt><dd>{selectedQuote.desired_outcome || 'Not provided'}</dd></div>
                  <div><dt>Current condition</dt><dd>{selectedQuote.current_condition || 'Not provided'}</dd></div>
                  <div><dt>Customer notes</dt><dd>{selectedQuote.customer_notes || 'Not provided'}</dd></div>
                </dl>
                <label className="admin-reply-field">
                  Reply
                  <textarea
                    onChange={(event) => setReply(event.target.value)}
                    placeholder="Write a reply. The server sends it to the stored enquiry email, not an address typed here."
                    rows={7}
                    value={reply}
                  />
                </label>
                <div className="admin-inline-actions">
                  <button disabled={busy || reply.trim().length === 0} type="button" onClick={() => void sendReply()}>Send Email</button>
                </div>
                <section>
                  <h3 className="customer-section-title">Communication history</h3>
                  {messages.length === 0 ? <p className="admin-empty-state">No emails sent from admin yet.</p> : null}
                  <ol className="admin-comm-history">
                    {messages.map((item) => (
                      <li key={item.id}>
                        <strong>{item.channel || 'email'} · {item.status}</strong>
                        <span>{item.subject || 'No subject'} · {item.sent_at || item.created_at}</span>
                        <p>{item.body || 'No message body stored for this event.'}</p>
                        {item.error_message ? <small>{item.error_message}</small> : null}
                      </li>
                    ))}
                  </ol>
                </section>
              </>
            ) : <p className="admin-empty-state">Select a quote request.</p>}
          </article>
        </section>
      </main>
    </div>
  )
}
